import React from 'react';
import { Box, Paper, Radio } from '@mui/material';
import { alpha } from '@mui/material/styles';

// import {
//   DataGrid,
//   GridColDef,
//   GridRowId,
//   GridRowParams,
//   GridValidRowModel,
// } from '@mui/x-data-grid';
import {
  DataGrid,
  GridColDef,
  GridRowId,
  GridRowParams,
  GridRowSelectionModel,
  GridValidRowModel,
} from '@mui/x-data-grid';

import FDataGridEmptyOverlay from './FDataGridEmptyOverlay';
import FDataGridHeader from './FDataGridHeader';
import FDataGridToolbar from './FDataGridToolbar';

import {
  FDataGridProps,
  FDataGridSelectionMode,
  FDataGridState,
} from './FDataGrid.models';

class FDataGrid extends React.Component<
  FDataGridProps,
  FDataGridState
> {
  static defaultProps: Partial<FDataGridProps> = {
    loading: false,
    height: 420,
    autoHeight: false,

    selectionMode: 'none',
    masterDetail: false,
    showSelectionControl: true,
    persistSelection: true,

    showToolbar: true,
    showQuickFilter: false,
    showColumnSelector: true,
    showFilterButton: true,
    showDensitySelector: true,

    enableCsvExport: true,
    enablePrintExport: false,
    csvDelimiter: ',',
    csvUtf8WithBom: true,
    exportAllColumns: false,

    pageSizeOptions: [10, 25, 50, 100],
    density: 'standard',
    paginationMode: 'client',
    sortingMode: 'client',
    filterMode: 'client',
  };

  constructor(props: FDataGridProps) {
    super(props);

    this.state = {
      rowSelectionModel:
        this.externalSelectionModel(props),
    };
  }

  componentDidUpdate(
    prevProps: FDataGridProps,
  ) {
    if (
      prevProps.selectedRowId !==
      this.props.selectedRowId ||
      prevProps.selectedRowIds !==
      this.props.selectedRowIds ||
      prevProps.selectionMode !==
      this.props.selectionMode ||
      prevProps.masterDetail !==
      this.props.masterDetail
    ) {
      const next =
        this.externalSelectionModel(
          this.props,
        );

      if (
        !this.sameSelection(
          next,
          this.state.rowSelectionModel,
        )
      ) {
        this.setState({
          rowSelectionModel: next,
        });
      }
    }
  }

  private sameSelection = (
    left: GridRowSelectionModel,
    right: GridRowSelectionModel,
  ): boolean => {
    if (left.type !== right.type) {
      return false;
    }

    if (left.ids.size !== right.ids.size) {
      return false;
    }

    for (const id of left.ids) {
      if (!right.ids.has(id)) {
        return false;
      }
    }

    return true;
  };

  private selectionMode = (
    props: FDataGridProps =
      this.props,
  ): FDataGridSelectionMode => {
    if (props.masterDetail) {
      return 'single';
    }

    if (props.checkboxSelection) {
      return 'multiple';
    }

    return (
      props.selectionMode ||
      'none'
    );
  };

  private emptySelectionModel = (): GridRowSelectionModel => ({
    type: 'include',
    ids: new Set<GridRowId>(),
  });

  private externalSelectionModel = (
    props: FDataGridProps,
  ): GridRowSelectionModel => {
    const mode = this.selectionMode(props);

    if (mode === 'single') {
      return {
        type: 'include',
        ids:
          props.selectedRowId === null ||
            props.selectedRowId === undefined
            ? new Set<GridRowId>()
            : new Set<GridRowId>([
              props.selectedRowId,
            ]),
      };
    }

    if (mode === 'multiple') {
      return {
        type: 'include',
        ids: new Set<GridRowId>(
          props.selectedRowIds || [],
        ),
      };
    }

    return this.emptySelectionModel();
  };

  private selectedIds = (
    model: GridRowSelectionModel =
      this.state.rowSelectionModel,
  ): GridRowId[] => {
    return Array.from(model.ids);
  };

  private rowIdOf = (
    row: GridValidRowModel,
  ): GridRowId => {
    if (this.props.getRowId) {
      return this.props.getRowId(
        row,
      );
    }

    return (
      row.id ??
      row.rid ??
      row.recid ??
      row.rix ??
      row.recix
    );
  };

  private rowById = (
    id: GridRowId,
  ): GridValidRowModel | null =>
    (this.props.rows || []).find(
      (row) =>
        String(
          this.rowIdOf(row),
        ) === String(id),
    ) || null;

  private selectSingleRow = (
    row: GridValidRowModel,
  ) => {
    const id = this.rowIdOf(row);

    const model: GridRowSelectionModel = {
      type: 'include',
      ids: new Set<GridRowId>([id]),
    };

    this.setState({
      rowSelectionModel: model,
    });

    this.props.onSelectedRowChange?.(
      row,
      id,
    );
  };

  private selectionChanged = (
    model: GridRowSelectionModel,
  ) => {
    const mode = this.selectionMode();
    const ids = Array.from(model.ids);

    if (mode === 'single') {
      const id =
        ids.length > 0
          ? ids[ids.length - 1]
          : null;

      const normalizedModel: GridRowSelectionModel = {
        type: 'include',
        ids:
          id === null
            ? new Set<GridRowId>()
            : new Set<GridRowId>([id]),
      };

      this.setState({
        rowSelectionModel:
          normalizedModel,
      });

      this.props.onSelectedRowChange?.(
        id === null
          ? null
          : this.rowById(id),
        id,
      );

      return;
    }

    if (mode === 'multiple') {
      this.setState({
        rowSelectionModel: model,
      });

      const selectedRows = ids
        .map((id) => this.rowById(id))
        .filter(Boolean) as GridValidRowModel[];

      this.props.onSelectedRowsChange?.(
        selectedRows,
        ids,
      );

      return;
    }

    this.setState({
      rowSelectionModel:
        this.emptySelectionModel(),
    });
  };

  private rowClicked = (
    params: GridRowParams,
    event: any,
    details: any,
  ) => {
    if (
      this.selectionMode() ===
      'single'
    ) {
      this.selectSingleRow(
        params.row,
      );
    }

    this.props.onRowClick?.(
      params,
      event,
      details,
    );
  };

  private enhancedColumns =
    (): GridColDef[] => {
      if (
        this.selectionMode() !==
          'single' ||
        !this.props
          .showSelectionControl
      ) {
        return this.props.columns;
      }

      const radioColumn: GridColDef =
        {
          field:
            '__f_selection__',
          headerName: '',
          width: 52,
          minWidth: 52,
          maxWidth: 52,
          sortable: false,
          filterable: false,
          editable: false,
          disableColumnMenu: true,
          resizable: false,
          align: 'center',
          headerAlign: 'center',

          renderCell: (
            params,
          ) => {
            const checked =
              this.selectedIds().some(
                (id) =>
                  String(id) ===
                  String(
                    params.id,
                  ),
              );

            return (
              <Radio
                size="small"
                checked={checked}
                onClick={(
                  event,
                ) =>
                  event.stopPropagation()
                }
                onChange={() =>
                  this.selectSingleRow(
                    params.row,
                  )
                }
                inputProps={{
                  'aria-label':
                    `Select row ${String(
                      params.id,
                    )}`,
                }}
              />
            );
          },
        };

      return [
        radioColumn,
        ...this.props.columns,
      ];
    };

  private rowClassName = (
    params: GridRowParams,
  ): string => {
    const supplied =
      this.props
        .getRowClassName?.(
          params as any,
        ) || '';

    const selected =
      this.selectedIds().some(
        (id) =>
          String(id) ===
          String(params.id),
      );

    return [
      supplied,
      selected
        ? 'fgrid-row-selected'
        : '',
    ]
      .filter(Boolean)
      .join(' ');
  };

  private renderToolbar = () => (
    <FDataGridToolbar
      showQuickFilter={
        this.props
          .showQuickFilter
      }
      showColumnSelector={
        this.props
          .showColumnSelector
      }
      showFilterButton={
        this.props
          .showFilterButton
      }
      showDensitySelector={
        this.props
          .showDensitySelector
      }
      enableCsvExport={
        this.props
          .enableCsvExport
      }
      enablePrintExport={
        this.props
          .enablePrintExport
      }
      csvFileName={
        this.props.csvFileName ||
        this.props.title ||
        'export'
      }
      csvDelimiter={
        this.props.csvDelimiter
      }
      csvUtf8WithBom={
        this.props
          .csvUtf8WithBom
      }
      exportAllColumns={
        this.props
          .exportAllColumns
      }
      sx={
        this.props.toolbarSx
      }
    />
  );

  private renderNoRowsOverlay =
    () => (
      <FDataGridEmptyOverlay
        emptyState={
          this.props
            .emptyState
        }
      />
    );

  render() {
    const mode =
      this.selectionMode();

    const showHeader =
      Boolean(
        this.props.title ||
          this.props.subtitle ||
          this.props.addRow ||
          this.props.saveAll ||
          this.props.refresh ||
          this.props
            .toolbarActions
            ?.length,
      );

    const showInternalToolbar =
      this.props.showToolbar !==
        false &&
      Boolean(
        this.props
          .showQuickFilter ||
          this.props
            .showColumnSelector !==
            false ||
          this.props
            .showFilterButton !==
            false ||
          this.props
            .showDensitySelector !==
            false ||
          this.props
            .enableCsvExport !==
            false ||
          this.props
            .enablePrintExport ===
            true,
      );

    const containerHeight =
      this.props.autoHeight
        ? 'auto'
        : this.props.height;

    return (
      <Paper
        elevation={1}
        sx={{
          width: '100%',
          overflow: 'hidden',
        }}
      >
        {showHeader && (
          <FDataGridHeader
            title={
              this.props.title
            }
            subtitle={
              this.props.subtitle
            }
            addRow={
              this.props.addRow
            }
            saveAll={
              this.props.saveAll
            }
            refresh={
              this.props.refresh
            }
            actions={
              this.props
                .toolbarActions
            }
            sx={
              this.props.headerSx
            }
          />
        )}

        <Box
          sx={{
            width: '100%',
            height:
              containerHeight,
          }}
        >
          <DataGrid
            apiRef={
              this.props.apiRef
            }

            rows={
              this.props.rows ||
              []
            }

            columns={
              this.enhancedColumns()
            }

            loading={
              this.props.loading
            }

            autoHeight={
              this.props.autoHeight
            }

            getRowId={
              this.props.getRowId ||
              ((row) =>
                this.rowIdOf(
                  row,
                ))
            }

            rowSelectionModel={
              this.state
                .rowSelectionModel as any
            }

            onRowSelectionModelChange={
              this.selectionChanged
            }

            checkboxSelection={
              mode === 'multiple'
            }

            disableRowSelectionOnClick={
              this.props
                .disableRowSelectionOnClick ??
              mode === 'none'
            }

            keepNonExistentRowsSelected={
              this.props
                .persistSelection
            }

            onRowClick={
              this.rowClicked
            }

            onRowDoubleClick={
              this.props
                .masterDetail
                ? undefined
                : this.props
                    .onRowDoubleClick
            }

            getRowClassName={
              this.rowClassName
            }

            processRowUpdate={
              this.props
                .processRowUpdate
            }

            onProcessRowUpdateError={
              this.props
                .onProcessRowUpdateError
            }

            pageSizeOptions={
              this.props
                .pageSizeOptions
            }

            initialState={
              this.props
                .initialState
            }

            paginationMode={
              this.props
                .paginationMode
            }

            rowCount={
              this.props.rowCount
            }

            paginationModel={
              this.props
                .paginationModel
            }

            onPaginationModelChange={
              this.props
                .onPaginationModelChange
            }

            sortingMode={
              this.props
                .sortingMode
            }

            sortModel={
              this.props.sortModel
            }

            onSortModelChange={
              this.props
                .onSortModelChange
            }

            filterMode={
              this.props
                .filterMode
            }

            filterModel={
              this.props
                .filterModel
            }

            onFilterModelChange={
              this.props
                .onFilterModelChange
            }

            density={
              this.props.density
            }

            disableColumnMenu={
              this.props
                .disableColumnMenu
            }

            hideFooter={
              this.props
                .hideFooter
            }

            slots={{
              ...(showInternalToolbar
                ? {
                    toolbar:
                      this
                        .renderToolbar,
                  }
                : {}),

              noRowsOverlay:
                this
                  .renderNoRowsOverlay,

              ...this.props.slots,
            }}

            slotProps={
              this.props
                .slotProps
            }

            showToolbar={
              showInternalToolbar
            }

            experimentalFeatures={
              this.props
                .experimentalFeatures
            }

            sx={[
              (theme: any) => ({
                border: 0,

                '& .MuiDataGrid-columnHeaders':
                  {
                    backgroundColor:
                      theme.palette
                        .action
                        .hover,
                  },

                '& .MuiDataGrid-row':
                  {
                    cursor:
                      mode ===
                      'none'
                        ? 'default'
                        : 'pointer',
                  },

                '& .fgrid-row-selected':
                  {
                    backgroundColor:
                      alpha(
                        theme
                          .palette
                          .primary
                          .main,
                        0.12,
                      ),
                  },

                '& .fgrid-row-selected:hover':
                  {
                    backgroundColor:
                      alpha(
                        theme
                          .palette
                          .primary
                          .main,
                        0.18,
                      ),
                  },

                '& .MuiDataGrid-row.Mui-selected':
                  {
                    backgroundColor:
                      alpha(
                        theme
                          .palette
                          .primary
                          .main,
                        0.12,
                      ),
                  },

                '& .MuiDataGrid-row.Mui-selected:hover':
                  {
                    backgroundColor:
                      alpha(
                        theme
                          .palette
                          .primary
                          .main,
                        0.18,
                      ),
                  },

                '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within':
                  {
                    outline:
                      'none',
                  },
              }),

              ...(Array.isArray(
                this.props.sx,
              )
                ? this.props.sx
                : [
                    this.props
                      .sx || {},
                  ]),
            ]}
          />
        </Box>
      </Paper>
    );
  }
}

export default FDataGrid;
